import React, { useState, useEffect, ReactNode } from 'react';
import { Flex, Select, Spin, Tag } from 'antd';
import { SelectProps } from 'antd/es/select';
import debounce from 'lodash/debounce';
import { productFetch } from '@/apis/product';
import SText from '@/styles/SText';

interface OptionType<T> {
  label: ReactNode;
  value: string;
  extraProps: T;
}

interface RemoteSelectProps<T> extends SelectProps<string> {
  fetchOptions: (page: number, body: string) => Promise<OptionType<T>[]>;
  debounceTimeout?: number;
  onBlurFetch?: boolean;
  listenValue?: string;
}

function RemoteSelect<T>({
  fetchOptions,
  debounceTimeout = 0,
  onBlurFetch = false,
  listenValue,
  ...props
}: RemoteSelectProps<T>):JSX.Element {
  const [data, setData] = useState<OptionType<T>[]>([]);
  const [value, setValue] = useState<string | undefined>(undefined);
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchData(page);
  }, [page]);
  console.log('data',data)
  useEffect(() => {
    if(listenValue) {
      if(!data.some(item => item.value === listenValue)) {
        setFetching(true)
        productFetch(listenValue).then((res) => {
          const newData = {
            value: res.id,
            label: (
              <Flex justify="space-between">
                <SText text={res.name} />
                <Tag color="green">{res.code}</Tag>
              </Flex>
            ),
            extraProps: res as T
          }
          setData([...data, newData])
          setHasMore(data.length > 0);
        }).finally(() => setFetching(false))
      }
    }
  }, [listenValue])

  useEffect(() => {
    const handler = setTimeout(() => {
      setData([]);
      if(value !== undefined) value ? searchData(1) : fetchData(1)
    }, debounceTimeout);
    return () => clearTimeout(handler)
  },[value])

  const fetchData = debounce(async (page: number) => {
    setFetching(true)
    const newOptions = await fetchOptions(page, value || '');
    console.log('newOptions',newOptions)
    if(page === 1) {
      setData(newOptions)
    } else if(page > 1) {
      setData((prevData) => {
        const filteredData = listenValue ? prevData.filter(item => item.value !== listenValue) : prevData;    
        return [...filteredData, ...newOptions];
      })
    }
    setHasMore(newOptions.length > 0);
    setFetching(false);
  }, debounceTimeout);

  const searchData = (async(page: number) => {
    setFetching(true)
    const newOptions = await fetchOptions(page, value || '');
    if(page === 1) {
      setData(newOptions);
    } else {
      setData(prevData => [...prevData, ...newOptions])
    }
    setHasMore(newOptions.length > 0);
    setFetching(false);
  })

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (!fetching && hasMore && target.scrollTop + target.offsetHeight >= target.scrollHeight) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  return (
    <Select
      loading={fetching}
      showSearch
      value={value}
      filterOption={false}
      onSearch={(value) => setValue(value)}
      onBlur={() => value ? setValue('') : setPage(1)}
      onFocus={() => onBlurFetch ? page === 1 ? fetchData(1) : setPage(1) : null}
      options={data}
      onChange={(newValue) => setValue(newValue)}
      onPopupScroll={handleScroll}
      popupMatchSelectWidth={false}
      dropdownRender={menu => (
        <div>
          {fetching && <Spin size="small" style={{ width: '100%', display: 'flex', justifyContent: 'center', }} />}
          {menu}
        </div>
      )}
      {...props}
    >
    </Select>
  );
};

export default RemoteSelect;
export type { RemoteSelectProps };